import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { deriveStatus } from "@astroplanet/shared";
import { useAppState } from "../../context/AppStateContext";
import { useSessions } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { useNow } from "../../hooks/useNow";
import { formatTime, initials } from "../../lib/format";
import { onScan } from "../../lib/scanBus";
import type { SessionView } from "../../lib/types";
import { Avatar, Empty, Pill, SectionHeader, SessionStatusBadge } from "../ui/Badges";
import { useEffect } from "react";
import { CheckoutDialog, ExtendDialog } from "./SessionDialogs";
import { PostpaidCheckoutDialog } from "./PostpaidCheckout";
import { TimeRemaining } from "./TimeRemaining";

type Filter = "all" | "expiring" | "overdue";

/** A post-paid visit is never overdue: its block just rolls over; it is "expiring" while the current block is about to end. */
function liveStatus(s: SessionView, nowMs: number, warnMinutes: number) {
  if (s.billingMode === "postpaid" && s.postpaid) return (new Date(s.endAt).getTime() - nowMs) / 60_000 <= s.postpaid.notifyBeforeMinutes ? ("expiring" as const) : ("active" as const);
  return deriveStatus(new Date(s.endAt), new Date(nowMs), warnMinutes);
}

export function ActiveBandsTable() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { openChat } = useAppState();
  const { data, isLoading } = useSessions();
  const nowMs = useNow();
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [extend, setExtend] = useState<SessionView | null>(null);
  const [checkout, setCheckout] = useState<SessionView | null>(null);
  const [billing, setBilling] = useState<SessionView | null>(null);

  const rules = data?.rules;
  const sessions = data?.sessions;

  // Scanning a wristband that is in the park opens its checkout.
  useEffect(
    () =>
      onScan((code) => {
        const hit = sessions?.find((s) => s.wristbandTag.toLowerCase() === code.trim().toLowerCase());
        if (hit) (hit.billingMode === "postpaid" ? setBilling : setCheckout)(hit);
      }),
    [sessions],
  );

  const rows = useMemo(() => {
    if (!sessions || !rules) return [];
    const term = q.trim().toLowerCase();
    return sessions
      .map((s) => ({ s, live: liveStatus(s, nowMs, rules.warnMinutes) }))
      .filter(({ s, live }) => (filter === "all" || live === filter) && (!term || `${s.kid.name} ${s.wristbandTag} ${s.guardian.fullName} ${s.guardian.phone}`.toLowerCase().includes(term)));
  }, [sessions, rules, nowMs, filter, q]);

  const counts = useMemo(() => {
    const c = { expiring: 0, overdue: 0 };
    if (sessions && rules) for (const s of sessions) {
      const st = liveStatus(s, nowMs, rules.warnMinutes);
      if (st === "expiring") c.expiring++;
      if (st === "overdue") c.overdue++;
    }
    return c;
  }, [sessions, rules, nowMs]);

  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("table.title")}>
      <SectionHeader
        title={t("table.title")}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="green">{t("table.inPark", { count: sessions?.length ?? 0 })}</Pill>
            <input className="field !w-44 !py-1.5" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("table.search")} aria-label={t("table.search")} />
            <div className="flex gap-1 text-[10px] font-bold">
              {(["all", "expiring", "overdue"] as Filter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                  className={`rounded-lg px-2 py-1 ${filter === f ? "bg-cosmic-navy text-white" : f === "expiring" ? "border border-amber-200 bg-amber-50 text-amber-800" : f === "overdue" ? "border border-red-200 bg-red-50 text-red-700" : "bg-gray-100 text-gray-700"}`}
                >
                  {t(`table.filter.${f}`)}{f !== "all" && counts[f] > 0 ? ` (${counts[f]})` : ""}
                </button>
              ))}
            </div>
          </div>
        }
      />
      {isLoading ? (
        <Empty>{t("common.loading")}</Empty>
      ) : rows.length === 0 ? (
        <Empty>{t("table.empty")}</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-start text-xs">
            <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2.5 text-start">{t("table.explorer")}</th>
                <th className="px-2 py-2.5 text-start">{t("table.tag")}</th>
                <th className="px-2 py-2.5 text-start">{t("table.startEnd")}</th>
                <th className="px-2 py-2.5 text-start">{t("table.remaining")}</th>
                <th className="px-2 py-2.5 text-center">{t("table.status")}</th>
                <th className="px-3 py-2.5 text-end">{t("table.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
              {rows.map(({ s, live }) => (
                <tr key={s.id} className={live === "overdue" ? "bg-red-50/40" : live === "expiring" ? "bg-amber-50/30" : "hover:bg-purple-50/20"}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar text={initials(s.kid.name)} tone={live === "overdue" ? "red" : live === "expiring" ? "amber" : "green"} />
                      <div>
                        <div className="font-extrabold text-cosmic-navy">{s.kid.name}{s.billingMode === "postpaid" && <span className="ms-1.5 rounded-full bg-purple-100 px-1.5 py-0.5 text-[9px] font-black text-cosmic-purple">🧾 {t("postpaid.badge")}</span>}</div>
                        <div className="text-[10px] text-gray-400">{s.guardian.fullName} • <span className="num">{s.guardian.phone}</span></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3"><span className="num rounded-md bg-gray-100 px-2 py-0.5 font-mono font-black text-cosmic-navy">{s.wristbandTag}</span></td>
                  <td className="px-2 py-3 font-mono text-[11px] text-gray-500">
                    <div className="num">{formatTime(s.startAt, locale)}</div>
                    <div className={`num ${live === "overdue" ? "font-bold text-red-500" : "text-gray-400"}`}>{formatTime(s.endAt, locale)}</div>
                  </td>
                  <td className="px-2 py-3">{rules && <TimeRemaining session={s} rules={rules} />}</td>
                  <td className="px-2 py-3 text-center"><SessionStatusBadge status={live} /></td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" className="rounded-lg bg-emerald-50 p-1.5 text-emerald-700 hover:bg-emerald-100" title={t("table.whatsapp")} aria-label={t("table.whatsapp")} onClick={() => openChat(s.guardian.id)}>💬</button>
                      {s.billingMode !== "postpaid" && <button type="button" className="rounded-lg bg-purple-50 px-2 py-1 text-[10px] font-black text-cosmic-purple hover:bg-purple-100" onClick={() => setExtend(s)}>{t("table.extend")}</button>}
                      {s.billingMode === "postpaid" ? (
                        <button type="button" className="rounded-lg bg-cosmic-navy px-2.5 py-1 text-[10px] font-bold text-white hover:bg-cosmic-purple" onClick={() => setBilling(s)}>{t("postpaid.exitAndBill")}</button>
                      ) : (
                        <button type="button" className={`rounded-lg px-2.5 py-1 text-[10px] font-bold text-white ${live === "overdue" ? "bg-red-600 hover:bg-red-700" : "bg-cosmic-navy hover:bg-red-600"}`} onClick={() => setCheckout(s)}>{live === "overdue" ? t("table.collectExit") : t("table.exit")}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {extend && rules && <ExtendDialog session={extend} rules={rules} onClose={() => setExtend(null)} />}
      {checkout && rules && <CheckoutDialog session={checkout} rules={rules} onClose={() => setCheckout(null)} />}
      {billing && <PostpaidCheckoutDialog session={billing} onClose={() => setBilling(null)} />}
    </section>
  );
}
